import { useEffect, useRef, useState } from 'react';
import { Outlet, useLocation } from 'react-router-dom';
import { api } from '../api';
import { useResource } from './use-resource';
import { useSession } from './session-context';
import { Rail } from './rail';
import { TopBar } from './top-bar';
import { TabBar } from './tab-bar';
import { MoreSheet } from './more-sheet';
import { signOutAndBounce } from './sign-out-action';
import type { ShellBadges } from './nav-items';
import type { MeOverview, ThreadList } from '../mock/seeds/schema-types';
import './dash.css';

// The persistent customer-dashboard shell (DC60; CB-17 wiring file) — the customer
// design's own frame (DR-45; Home.dc.html L30-60 + Others.dc.html rail/topbar/tabbar):
//   ≥760 px: floating glass rail (rail.tsx) + a column with the sticky top bar
//            (top-bar.tsx: ⌘K palette, notification tray, account menu) and the page;
//   <760 px: the top bar's mobile variant + the bottom tab bar with the More sheet.
// Badges are real signals, re-checked on navigation: My bookings = bookings that need
// the customer (GET /me/overview needs_attention), Reviews = to_review_count, Messages
// = unread threads (GET /me/threads, shown in the account menu / More sheet). The
// notification count is owned by the top bar's tray feed. No role switcher and no
// session list (DR-38).
export function DashLayout() {
  const location = useLocation();
  const { user } = useSession();
  const [moreOpen, setMoreOpen] = useState(false);
  const mainRef = useRef<HTMLElement>(null);

  // The page column is the scroller (the window never scrolls, as in the design), so
  // a new page starts at its top.
  useEffect(() => {
    mainRef.current?.scrollTo?.(0, 0);
  }, [location.pathname]);

  const { data: overview } = useResource<MeOverview>((signal) => api.GET('/me/overview', { signal }), [location.pathname]);
  const { data: threads } = useResource<ThreadList>((signal) => api.GET('/me/threads', { signal }), [location.pathname]);

  const badges: ShellBadges = {
    bookings: overview?.needs_attention?.length ?? 0,
    reviews: overview?.to_review_count ?? 0,
    messages: threads?.total_unread ?? 0,
  };

  const onSignOut = () => {
    void signOutAndBounce();
  };

  return (
    <div className="app-shell">
      <Rail badges={badges} onSignOut={onSignOut} />
      <div className="app-col">
        <TopBar user={user} badges={badges} onSignOut={onSignOut} />
        <main className="app-main" ref={mainRef}>
          <div className="app-main__inner">
            <Outlet />
          </div>
        </main>
      </div>
      <TabBar badges={badges} moreOpen={moreOpen} onMore={() => setMoreOpen(true)} />
      <MoreSheet open={moreOpen} onClose={() => setMoreOpen(false)} badges={badges} user={user} onSignOut={onSignOut} />
    </div>
  );
}
